// src/pages/subjects/StudentSubjects.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { PageLoader } from '../../components/Loader';
import { socket } from '../../services/socket';

export default function StudentSubjects() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const fetchSubjectsRef = useRef(null);

  // Resolve the Student table ID linked to the authenticated user.
  useEffect(() => {
    let cancelled = false;

    const resolveStudentId = async () => {
      if (!user?.id) {
        if (!cancelled) {
          setError('User not authenticated.');
          setLoading(false);
        }
        return;
      }

      const existingStudentId =
        user.student_id ||
        user.studentId ||
        user.student?.id ||
        user.Student?.id;

      if (existingStudentId) {
        if (!cancelled) setStudentId(existingStudentId);
        return;
      }

      try {
        // apiClient supplies the base URL and current authentication token.
        const response = await apiClient.get(`/students/by-user/${user.id}`);
        const resolvedId = response.data?.data?.id;

        if (!resolvedId) {
          throw new Error('Could not find student profile for this user.');
        }

        if (!cancelled) setStudentId(resolvedId);
      } catch (requestError) {
        if (cancelled) return;
        console.error('Error fetching student ID:', requestError);
        setError(
          requestError.response?.data?.message ||
            requestError.message ||
            'Could not retrieve student information.'
        );
        setLoading(false);
      }
    };

    resolveStudentId();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const fetchStudentSubjects = useCallback(
    async (silent = false) => {
      if (!studentId) return;

      try {
        if (!silent) setLoading(true);

        // subject.routes.js is mounted at /subjects, making the complete path:
        // /api/v1/subjects/students/:studentId/subjects
        const response = await apiClient.get(
          `/subjects/students/${studentId}/subjects`,
          {
            // A query value safely prevents stale browser/proxy responses.
            // Do not send Expires/Pragma/Cache-Control request headers because
            // they trigger a CORS preflight unless explicitly allow-listed.
            params: { _t: Date.now() },
          }
        );

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to load subjects.');
        }

        setSubjects(Array.isArray(response.data.data) ? response.data.data : []);
        setError(null);
      } catch (requestError) {
        console.error('Error fetching student subjects:', requestError);
        setError(
          requestError.response?.data?.message ||
            requestError.message ||
            'Unable to fetch subjects. Please try again.'
        );
        if (!silent) toast.error('Could not load your subjects.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [studentId]
  );

  useEffect(() => {
    fetchSubjectsRef.current = fetchStudentSubjects;
  }, [fetchStudentSubjects]);

  useEffect(() => {
    if (studentId) fetchStudentSubjects(false);
  }, [studentId, fetchStudentSubjects]);

  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;

    const refreshSubjects = (event = {}) => {
      if (
        event.schoolId &&
        user?.school_id &&
        String(event.schoolId) !== String(user.school_id)
      ) {
        return;
      }

      fetchSubjectsRef.current?.(true);
    };

    const handleReconnect = () => fetchSubjectsRef.current?.(true);

    socket.on('subject:updated', refreshSubjects);
    socket.on('class:updated', refreshSubjects);
    socket.on('teacher:updated', refreshSubjects);
    socket.on('connect', handleReconnect);

    return () => {
      socket.off('subject:updated', refreshSubjects);
      socket.off('class:updated', refreshSubjects);
      socket.off('teacher:updated', refreshSubjects);
      socket.off('connect', handleReconnect);
    };
  }, [user?.school_id]);

  if (loading) return <PageLoader />;

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg font-semibold text-red-500">⚠️ {error}</div>
        <button
          type="button"
          onClick={() => fetchStudentSubjects(false)}
          className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const showCode = subjects.some((subject) => subject.code);
  const showSchedule = subjects.some((subject) => subject.schedule);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          My Subjects
        </h1>
        <p className="mt-1 text-sm text-gray-500 sm:text-base">
          Subjects you are currently enrolled in.
        </p>
      </div>

      {subjects.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <BookOpenIcon className="mx-auto h-16 w-16 text-gray-300" />
          <p className="mt-4 text-lg text-gray-500">
            You are not enrolled in any subjects yet.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Subject', 'Teacher', 'Phone'].map((heading) => (
                    <th
                      key={heading}
                      className="border-r border-gray-200 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                  {showCode && (
                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Code
                    </th>
                  )}
                  {showSchedule && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Schedule
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {subjects.map((subject, index) => (
                  <tr
                    key={subject.id || `${subject.name}-${index}`}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap border-r border-gray-100 px-4 py-3 text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap border-r border-gray-100 px-4 py-3 text-sm font-medium text-gray-900">
                      {subject.name}
                    </td>
                    <td className="whitespace-nowrap border-r border-gray-100 px-4 py-3 text-sm text-gray-700">
                      {subject.teacher_name || '—'}
                    </td>
                    <td className="whitespace-nowrap border-r border-gray-100 px-4 py-3 text-sm text-gray-600">
                      {subject.teacher_phone || '—'}
                    </td>
                    {showCode && (
                      <td className="whitespace-nowrap border-r border-gray-100 px-4 py-3 text-sm text-gray-500">
                        {subject.code || '—'}
                      </td>
                    )}
                    {showSchedule && (
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {subject.schedule || '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}